<template>
  <div class="quote-details-unique-pricing">
    <QuoteHeader
      :organisation-name="quote.organisation.name"
      :quote-number="quote.quote_number"
    />
    <div class="quote-details-unique-pricing__content">
      <div class="quote-details-unique-pricing__sections">
        <PricingSection
          :title="$t('quote_details_unique_pricing.subscription.title')"
          :description="$t('quote_details_unique_pricing.subscription.description')"
          :products="subscriptionProducts"
          :total-label="$t('quote_details_unique_pricing.total_subscription_label')"
          :total-amount="subscriptionTotal"
          has-rounded-header
        >
          <template #header-content>
            <MonthlyUsersFluctuactionBreakdown
              v-if="hasFluctuationsSeasonality"
              :seasonality-fluctuation-months="seasonalityFluctuationMonths"
              :additional-user-price="additionalUserPriceText"
              :locale="quote.locale"
            />
            <div class="subscription-header__infos">
              <div class="subscription-header__infos-column">
                <div
                  v-if="!hasFluctuationsSeasonality"
                  class="sk-text-small-regular subscription-header__infos-item"
                >
                  <span class="text--grey">
                    {{ $t('quote_details_unique_pricing.info.included_users.label') }}
                    <span class="text--black">
                      {{ includedUsersLabel }}
                    </span>
                  </span>
                  <span class="sk-text-x-small-regular text--grey">
                    {{ additionalUserPriceText }}
                  </span>
                </div>
                <span class="sk-text-small-regular text--grey">
                  {{ $t('quote_details_unique_pricing.info.workspace_count') }}
                  <span class="text--black">
                    {{ workspaceCount }}
                  </span>
                  <span
                    v-if="hasOnlyOneWorkspace"
                    class="sk-text-x-small-regular"
                  >
                    {{
                      $t(
                        'quote_details_unique_pricing.info.workspace_price',
                        { workspacePrice }
                      )
                    }}
                  </span>
                </span>
              </div>
              <div class="subscription-header__infos-column--right">
                <span class="sk-text-small-regular text--grey">
                  {{ $t('quote_details_unique_pricing.info.commitment.label') }}
                  <span class="text--black">
                    {{ commitmentValue }}
                  </span>
                </span>
                <span class="sk-text-small-regular text--grey">
                  {{ $t('quote_details_unique_pricing.info.payment_frequency.label') }}
                  <span class="text--black">
                    {{ paymentFrequencyValue }}
                  </span>
                </span>
              </div>
            </div>
          </template>
          <template
            v-if="!hasOnlyOneWorkspace"
            #workspace-row
          >
            <WorkspaceRow
              :name="workspaceProduct.name"
              :discount-tag="workspaceProduct.discountTag"
              :original-price="workspaceProduct.originalPrice"
              :final-price="workspaceProduct.finalPrice"
              :workspace-names="workspaceNames"
            />
          </template>
        </PricingSection>
        <PricingSection
          v-if="chargesProducts.length > 0"
          :title="$t('quote_details_unique_pricing.charges.title')"
          :description="$t('quote_details_unique_pricing.charges.description')"
          :products="chargesProducts"
          :total-label="$t('quote_details_unique_pricing.total_charges_label')"
          :total-amount="chargesTotal"
        />
      </div>
      <div class="divider" />
      <QuoteTotalSection
        :excluded-tax-total-amount="excludedTaxTotalAmount"
        :subscription-tax-amount="subscriptionTaxAmount"
        :charges-tax-amount="chargesTaxAmount"
        :total-amount-with-tax="totalAmountWithTax"
        :tax-percentage="taxPercentage"
      />
    </div>
  </div>
</template>

<script>
import {
  QuoteLineItemType,
  QuoteLineItemSeasonalityType,
} from '@skelloapp/svc-billing-automation-sdk';

import {
  extractPaymentFrequency,
  extractPrice,
} from '@/shared/utils/method_helper';

import QuoteHeader from './QuoteHeader.vue';
import QuoteTotalSection from './QuoteTotalSection.vue';
import PricingSection from './PricingSection.vue';
import WorkspaceRow from './WorkspaceRow.vue';

import MonthlyUsersFluctuactionBreakdown from '@/shared/components/MonthlyUsersFluctuactionBreakdown/index.vue';

export default {
  name: 'QuoteDetailsUniquePricing',
  components: {
    QuoteHeader,
    MonthlyUsersFluctuactionBreakdown,
    QuoteTotalSection,
    PricingSection,
    WorkspaceRow,
  },
  props: {
    quote: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      chargesQuoteLineItems: [],
      optionQuoteLineItem: {},
      packQuoteLineItem: {},
      workspaceQuoteLineItem: {},
    };
  },
  computed: {
    hasFluctuationsSeasonality() {
      const { seasonality } = this.packQuoteLineItem;
      return seasonality === QuoteLineItemSeasonalityType.FLUCTUATIONS;
    },
    seasonalityFluctuationMonths() {
      return this.packQuoteLineItem.seasonality_fluctuation_months ?? {};
    },
    hasOnlyOneWorkspace() {
      return this.workspaceCount === 1;
    },
    workspaceCount() {
      return this.quote.organisation.shops.length;
    },
    workspaceNames() {
      return this.quote.organisation.shops
        .map(shop => shop.name);
    },
    additionalUserPrice() {
      return this.packQuoteLineItem.additional_user_price +
        (this.optionQuoteLineItem?.additional_user_price ?? 0);
    },
    additionalUserPriceText() {
      return this.$t('quote_details_unique_pricing.info.additional_user_price', {
        price: this.additionalUserPrice.toFixed(2),
      });
    },
    includedUsersLabel() {
      const maxUser = this.packQuoteLineItem.max_users;
      return this.$t('quote_details_unique_pricing.info.included_users.value', {
        maxUser,
      });
    },
    commitmentValue() {
      const commitment = this.quote.organisation.contract_terms.commitment.toLowerCase().split(' ').join('_');
      return this.$t(`quote_details_unique_pricing.info.commitment.value.${commitment}`);
    },
    paymentFrequencyValue() {
      const paymentFrequency = extractPaymentFrequency(this.quote);
      return this.$t(`quote_details_unique_pricing.info.payment_frequency.value.${paymentFrequency}`);
    },
    workspacePrice() {
      return extractPrice(this.quote, this.workspaceQuoteLineItem.unit_mrr);
    },
    subscriptionProducts() {
      if (this.hasOnlyOneWorkspace) {
        return this.getOneLineSubscriptionViewModel();
      }

      return [this.packQuoteLineItem, this.optionQuoteLineItem]
        .filter(product => product && Object.keys(product).length > 0)
        .map(product => this.mapProductToViewModel(product));
    },
    workspaceProduct() {
      const customName = this.$tc(
        'quote_details_unique_pricing.workspace_name',
        this.workspaceCount,
      );
      return this.mapProductToViewModel(this.workspaceQuoteLineItem, customName);
    },
    chargesProducts() {
      return this.chargesQuoteLineItems
        .map(product => this.mapProductToViewModel(product));
    },
    subscriptionTotal() {
      return extractPrice(this.quote, this.quote.totals.total_amount_with_discount);
    },
    chargesTotal() {
      return extractPrice(this.quote, this.quote.totals.total_charges_with_discount);
    },
    excludedTaxTotalAmount() {
      return extractPrice(this.quote, this.quote.totals.total_amount_first_payment_with_discount);
    },
    subscriptionTaxAmount() {
      return extractPrice(this.quote, this.quote.totals.total_tax);
    },
    chargesTaxAmount() {
      if (this.quote.totals?.total_tax_charges > 0) {
        return extractPrice(this.quote, this.quote.totals.total_tax_charges);
      }

      return null;
    },
    totalAmountWithTax() {
      return extractPrice(this.quote, this.quote.totals.total_amount_first_payment_with_tax);
    },
    taxPercentage() {
      return this.workspaceQuoteLineItem.tax_rate;
    },
  },
  created() {
    this.chargesQuoteLineItems = this.quote.quote_line_items.filter(
      lineItem => lineItem.type === QuoteLineItemType.CHARGE,
    );

    this.packQuoteLineItem = this.quote.quote_line_items.find(
      lineItem => lineItem.type === QuoteLineItemType.PACK,
    );

    this.optionQuoteLineItem = this.quote.quote_line_items.find(
      lineItem => lineItem.type === QuoteLineItemType.OPTION,
    );

    this.workspaceQuoteLineItem = this.quote.quote_line_items.find(
      lineItem => lineItem.type === QuoteLineItemType.WORKSPACE,
    );
  },
  methods: {
    getOneLineSubscriptionViewModel() {
      const totalAmount = this.packQuoteLineItem.total_amount +
        (this.workspaceQuoteLineItem.total_amount ?? 0) +
        (this.optionQuoteLineItem?.total_amount ?? 0);
      const totalAmountWithDiscount = this.packQuoteLineItem.total_amount_with_discount +
        (this.workspaceQuoteLineItem.total_amount_with_discount ?? 0) +
        (this.optionQuoteLineItem?.total_amount_with_discount ?? 0);

      const lineItem = {
        ...this.packQuoteLineItem,
        total_amount: totalAmount,
        total_amount_with_discount: totalAmountWithDiscount,
      };

      const hasOption = this.optionQuoteLineItem &&
        Object.keys(this.optionQuoteLineItem).length > 0;
      const customName = hasOption ?
        this.$t('quote_details_unique_pricing.pack_with_option', {
          packName: this.packQuoteLineItem.title,
          optionName: this.optionQuoteLineItem.title,
        }) : null;

      return [this.mapProductToViewModel(lineItem, customName)];
    },
    /**
     * Maps a product line item to the view model format used by ProductRow components
     * @param {Object} product - The quote line item product
     * @param {String|null} customName - Optional custom name to override product.title
     * @returns {Object} View model with id, name, discountTag, originalPrice, finalPrice
     */
    mapProductToViewModel(product, customName = null) {
      if (!product) return null;

      const discountTag = this.getDiscountTag(product);
      return {
        id: product.id,
        name: customName || product.title,
        discountTag,
        originalPrice: discountTag ?
          extractPrice(this.quote, product.total_amount) :
          null,
        finalPrice: extractPrice(this.quote, product.total_amount_with_discount),
      };
    },
    /**
     * Generates a discount tag string if the product has a discount
     * @param {Object} product - The quote line item product
     * @returns {String|null} Formatted discount tag or null
     */
    getDiscountTag(product) {
      if (!product?.discounts?.length) return null;

      const discount = product.discounts[0]?.discount_amount;
      return discount ?
        this.$t('quote_details_unique_pricing.discounted_price_tag', {
          discountedPrice: discount,
        }) :
        null;
    },
  },
};
</script>

<style lang="scss">
@use './shared';

.quote-details-unique-pricing {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.quote-details-unique-pricing__content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
  border: 1px solid $sk-grey-10;
  border-radius: 8px;
}

.quote-details-unique-pricing__sections {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.subscription-header__infos {
  display: flex;
  gap: 16px;
}

.subscription-header__infos-column {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.subscription-header__infos-column--right {
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: right;
}

.subscription-header__infos-item {
  display: flex;
  gap: 4px;
  align-items: baseline;
}

.divider {
  height: 1px;
  width: 100%;
  background-color: $sk-grey-10;
}
</style>
